import React, { useState, useEffect } from 'react';
import { getAllChangelogEvents, searchChangelogEvents } from '../../../services/api';
import MatchAnalysis from './MatchAnalysis';
import SeverityBadge from './SeverityBadge';
import { Button } from '../../../components/ui/button';
import { 
  Search, 
  ChevronDown, 
  ChevronUp,
  GitPullRequest,
  Settings,
  Terminal,
  AlertTriangle,
  ExternalLink,
  Clock,
  Code2,
  Sparkles
} from 'lucide-react';

const DemoSection = () => {
  // Separate state for expanded status and selected item
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [incidentDescription, setIncidentDescription] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [displayedEvents, setDisplayedEvents] = useState([]);
  const [isSearchResult, setIsSearchResult] = useState(false);
  const [error, setError] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const response = await getAllChangelogEvents(1, 5);
        // Handle both array and object responses
        const events = Array.isArray(response) ? response : 
                      response?.events ? response.events : [];
        setDisplayedEvents(events);
        setIsSearchResult(false);
        setError(null);
      } catch (error) {
        console.error('Failed to fetch initial changelog events:', error);
        setError('Failed to load events. Please try again later.');
        setDisplayedEvents([]);
      }
    };

    loadInitialData();
  }, []);

  const handleSearch = async () => {
    // Reset selection state before searching
    setSelectedItem(null);
    setExpandedEventId(null);
    
    if (!incidentDescription.trim()) {
      try {
        const response = await getAllChangelogEvents(1, 5);
        const events = Array.isArray(response) ? response : 
                      response?.events ? response.events : [];
        setDisplayedEvents(events);
        setIsSearchResult(false);
        setError(null);
      } catch (error) {
        console.error('Failed to fetch all changelog events:', error);
        setError('Failed to load events. Please try again later.');
        setDisplayedEvents([]);
      }
      return;
    }
    
    setIsAnalyzing(true);
    try {
      const searchResults = await searchChangelogEvents(incidentDescription);
      // Take only the top 5 search results for the demo
      const validResults = searchResults
        .filter(item => item && item.changelog_event)
        .slice(0, 5);
      setDisplayedEvents(validResults);
      setIsSearchResult(true);
      setError(null);
    } catch (error) {
      console.error('Failed to search changelog events:', error);
      setError('Search failed. Please try again.');
      setDisplayedEvents([]);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getSourceTypeIcon = (sourceType) => {
    switch (sourceType?.toLowerCase()) {
      case 'git':
      case 'github':
        return <GitPullRequest className="w-5 h-5 text-green-600" />;
      case 'config':
        return <Settings className="w-5 h-5 text-blue-600" />;
      case 'script':
        return <Terminal className="w-5 h-5 text-purple-600" />;
      case 'jira':
        return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      default:
        return <Code2 className="w-5 h-5 text-gray-600" />;
    }
  };

  const handleEventClick = (event, item) => {
    if (!event || !event.id) {
      console.error('Invalid event data:', event);
      return;
    }
    setExpandedEventId(expandedEventId === event.id ? null : event.id);
    setSelectedItem(selectedItem?.id === event.id ? null : item);
  };

  return (
    <div id="demo" className="relative bg-gradient-to-b from-gray-50 to-white py-24">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply opacity-10 animate-blob"></div>
        <div className="absolute -bottom-40 left-1/3 w-96 h-96 bg-indigo-500 rounded-full mix-blend-multiply opacity-10 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Introduction Section */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-full text-blue-700 text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4 mr-2" />
            Interactive Demo
          </div>
          
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Find System Changes with Natural Language
          </h2>
          
          <p className="text-xl text-gray-600">
            Describe what you're looking for in plain English, and our semantic search will find the relevant changes.
          </p>
        </div>

        {/* Search Section */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl border-0 p-8">
            {/* Search Input */}
            <div className="mb-8">
              <div className="space-y-4">
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <Search className="w-4 h-4" />
                  <span>Try describing:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Changes to the authentication system",
                    "Database performance updates",
                    "Frontend deployment issues"
                  ].map((example) => (
                    <button
                      key={example}
                      onClick={() => setIncidentDescription(example)}
                      className="px-3 py-1 text-sm bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-full transition-colors"
                    >
                      "{example}"
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="relative mt-4">
                <textarea
                  className="w-full min-h-[120px] p-6 text-lg rounded-xl border-2 border-blue-100 focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all resize-none"
                  placeholder="Describe what you're looking for..."
                  value={incidentDescription}
                  onChange={(e) => setIncidentDescription(e.target.value)}
                />
                <Button
                  size="lg"
                  className="absolute bottom-4 right-4 bg-blue-600 hover:bg-blue-700"
                  onClick={handleSearch}
                  disabled={isAnalyzing}
                >
                  {isAnalyzing ? (
                    <>
                      <Clock className="mr-2 h-5 w-5 animate-spin" />
                      Finding Changes...
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-5 w-5" />
                      Search Changes
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Results Section */}
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {isSearchResult ? 'Search Results' : 'Recent Changes'}
                  {displayedEvents.length > 0 && (
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      ({displayedEvents.length} {displayedEvents.length === 1 ? 'event' : 'events'})
                    </span>
                  )}
                </h3>
              </div>

              <div className="divide-y divide-gray-100 overflow-y-auto max-h-96">
                {error ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {error}
                    </h3>
                  </div>
                ) : displayedEvents.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <Search className="w-12 h-12 text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {isSearchResult ? 'No matching changes found' : 'No recent changes'}
                    </h3>
                    <p className="text-gray-500 max-w-sm">
                      {isSearchResult 
                        ? 'Try adjusting your search terms or checking a different time period.'
                        : 'There are no system changes to display at this time.'}
                    </p>
                  </div>
                ) : (
                  displayedEvents.map((item) => {
                    // Handle both direct events and search results
                    const event = isSearchResult && item.changelog_event ? item.changelog_event : item;
                    
                    // Skip invalid events
                    if (!event || !event.id) {
                      console.error('Invalid event data:', item);
                      return null;
                    }

                    return (
                      <div key={event.id} className="bg-white">
                        {/* Main row */}
                        <div 
                          className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => handleEventClick(event, item)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="p-2 bg-gray-50 rounded-lg">
                                {getSourceTypeIcon(event.source_type)}
                              </div>
                              <div>
                                <h4 className="text-lg font-medium text-gray-900">{event.title}</h4>
                                <p className="text-sm text-gray-500">
                                  {formatDate(event.event_date || event.created_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-4">
                              {isSearchResult && item.score != null && (
                                <span className="text-sm text-gray-500">
                                  Match: {Math.round(item.score * 100)}%
                                </span>
                              )}
                              <SeverityBadge severity={event.severity} />
                              {event.is_critical && (
                                <AlertTriangle className="w-5 h-5 text-red-500" />
                              )}
                              {expandedEventId === event.id ? (
                                <ChevronUp className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Expanded details */}
                        {expandedEventId === event.id && (
                          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                            <div className="space-y-4">
                              <div>
                                <h5 className="font-medium text-gray-900 mb-2">Description</h5>
                                <p className="text-gray-600 whitespace-pre-wrap">{event.description}</p>
                              </div>

                              {event.tags && event.tags.length > 0 && (
                                <div>
                                  <h5 className="font-medium text-gray-900 mb-2">Tags</h5>
                                  <div className="flex flex-wrap gap-2">
                                    {event.tags.map((tag, index) => (
                                      <span
                                        key={index}
                                        className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {event.source_link && (
                                <div>
                                  <h5 className="font-medium text-gray-900 mb-2">Source</h5>
                                  <a
                                    href={event.source_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center text-blue-600 hover:text-blue-800"
                                  >
                                    View Source
                                    <ExternalLink className="w-4 h-4 ml-1" />
                                  </a>
                                </div>
                              )}

                              {event.metadata && Object.keys(event.metadata).length > 0 && (
                                <div>
                                  <h5 className="font-medium text-gray-900 mb-2">Additional Details</h5>
                                  <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                                    {JSON.stringify(event.metadata, null, 2)}
                                  </pre>
                                </div>
                              )}

                              {isSearchResult && item.metadata && (
                                <MatchAnalysis 
                                  item={{
                                    metadata: {
                                      field_scores: item.metadata.field_scores || {},
                                      temporal_decay: item.metadata.temporal_decay || 1.0,
                                      event_date: event.event_date
                                    },
                                    score: item.score || 0
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoSection;